(() => {
  const STORAGE_KEY = 'gangava:recently_viewed:v1';
  const MAX_STORED = 12;
  const MAX_VISIBLE = 6;

  const safeParse = (value, fallback) => {
    try {
      return JSON.parse(value) || fallback;
    } catch (error) {
      return fallback;
    }
  };

  const saveHistory = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_STORED)));
    } catch (error) {
      // Private browsing or a full storage quota should not break the product page.
    }
  };

  const readStoredHistory = () => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  };

  const normalizeItem = (item) => {
    if (!item || typeof item !== 'object') return null;
    const handle = String(item.handle || '').trim();
    const url = String(item.url || '').trim();
    if (!handle || !url || !url.startsWith('/products/')) return null;

    return {
      handle,
      id: String(item.id || handle),
      variantId: String(item.variantId || ''),
      title: String(item.title || handle),
      url: url.split('?')[0].replace(/\/$/, ''),
      image: String(item.image || ''),
      price: String(item.price || ''),
      priceCents: String(item.priceCents || ''),
      compareAtPrice: String(item.compareAtPrice || ''),
      compareAtPriceCents: String(item.compareAtPriceCents || ''),
      estado: String(item.estado || ''),
      estadoId: String(item.estadoId || ''),
      defecto: Boolean(item.defecto),
      available: item.available === true
    };
  };

  const getHistory = () => {
    const stored = safeParse(readStoredHistory(), []);
    if (!Array.isArray(stored)) {
      saveHistory([]);
      return [];
    }

    const cleaned = [];
    const seen = new Set();
    stored.forEach((item) => {
      const normalized = normalizeItem(item);
      if (!normalized || seen.has(normalized.handle)) return;
      seen.add(normalized.handle);
      cleaned.push(normalized);
    });

    if (cleaned.length !== stored.length || cleaned.some((item, index) => item.handle !== stored[index]?.handle)) {
      saveHistory(cleaned);
    }

    return cleaned;
  };

  const readCurrentProduct = (root) => normalizeItem({
    handle: root.dataset.currentHandle,
    id: root.dataset.currentId,
    variantId: root.dataset.currentVariantId,
    title: root.dataset.currentTitle,
    url: root.dataset.currentUrl,
    image: root.dataset.currentImage,
    price: root.dataset.currentPrice,
    priceCents: root.dataset.currentPriceCents,
    compareAtPrice: root.dataset.currentCompareAtPrice,
    compareAtPriceCents: root.dataset.currentCompareAtPriceCents,
    estado: root.dataset.currentEstado,
    estadoId: root.dataset.currentEstadoId,
    defecto: root.dataset.currentDefecto === 'true',
    available: root.dataset.currentAvailable === 'true'
  });

  const productJsonUrl = (item) => `${item.url}.js`;

  const refreshItem = async (item, currency) => {
    try {
      const response = await fetch(productJsonUrl(item), {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return null;

      const product = await response.json();
      const variant = product && product.variants && product.variants[0];
      if (!product || !variant || !variant.id) return null;

      return {
        ...item,
        id: String(product.id || item.id),
        variantId: String(variant.id),
        title: String(product.title || item.title),
        image: String(product.featured_image || item.image),
        available: Boolean(product.available),
        price: formatMoney(variant.price, currency),
        priceCents: String(variant.price || ''),
        compareAtPrice: variant.compare_at_price && variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price, currency) : '',
        compareAtPriceCents: variant.compare_at_price && variant.compare_at_price > variant.price ? String(variant.compare_at_price) : '',
        currency: currency || item.currency || ''
      };
    } catch (error) {
      return item;
    }
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const escapeAttr = escapeHtml;

  const parseCents = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const formatMoney = (cents, currency) => {
    const amount = parseCents(cents) / 100;
    try {
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: currency || 'EUR' }).format(amount);
    } catch (error) {
      return amount.toFixed(2).replace('.', ',') + ' €';
    }
  };

  const discountData = (item) => {
    const price = parseCents(item.priceCents);
    const compare = parseCents(item.compareAtPriceCents);
    if (!item.available || !price || !compare || compare <= price) return null;
    const saving = compare - price;
    const percent = Math.round((saving * 100) / compare);
    return { saving, percent };
  };

  const renderCard = (item) => {
    const card = document.createElement('article');
    card.className = 'gangava-product-card';
    card.dataset.gangavaAvailabilityCard = '';
    card.dataset.gangavaAvailable = item.available ? 'true' : 'false';

    const imageMarkup = item.image
      ? `<img src="${escapeAttr(item.image)}" alt="${escapeAttr(item.title)}" loading="lazy">`
      : '<div class="gangava-placeholder">Imagen pendiente</div>';
    const soldMarkup = item.available === false
      ? '<span class="gangava-product-card__availability">AGOTADO</span>'
      : '';
    const estadoId = String(item.estadoId || '').trim();
    const stateAttribute = estadoId ? ` data-gangava-state="${escapeAttr(estadoId)}"` : '';
    const estadoMarkup = item.estado
      ? `<span class="badge gangava-card-status"${stateAttribute}>${escapeHtml(item.estado)}</span>`
      : '';
    const defectMarkup = item.defecto
      ? '<span class="badge gangava-card-defect">Defectos</span>'
      : '';
    const discount = discountData(item);
    const discountMarkup = discount
      ? `<span class="badge badge--bottom-left gangava-card-discount">-${discount.percent}%</span>`
      : '';
    const badgesMarkup = [estadoMarkup, defectMarkup, discountMarkup].filter(Boolean).join(' ');
    const compareMarkup = item.compareAtPrice
      ? `<span class="gangava-price__compare">${escapeHtml(item.compareAtPrice)}</span>`
      : '';
    const savingMarkup = discount
      ? `<div class="gangava-card-saving">Te ahorras ${escapeHtml(formatMoney(discount.saving, item.currency))} (-${discount.percent}%)</div>`
      : '';
    const stockClass = item.available === false ? ' gangava-product-card__stock--sold' : '';
    const canonicalUrl = window.location.origin + item.url;
    const favoriteMarkup = `
      <button
        class="gv-favorite-button gv-favorite-button--card"
        type="button"
        data-gv-favorite-button
        data-product-id="${escapeAttr(item.id)}"
        data-product-variant-id="${escapeAttr(item.variantId)}"
        data-product-handle="${escapeAttr(item.handle)}"
        data-product-title="${escapeAttr(item.title)}"
        data-product-url="${escapeAttr(item.url)}"
        data-product-image="${escapeAttr(item.image)}"
        data-product-price="${escapeAttr(item.price)}"
        data-product-compare-at-price="${escapeAttr(item.compareAtPrice)}"
        data-product-estado="${escapeAttr(item.estado)}"
        data-product-defecto="${item.defecto ? 'true' : ''}"
        data-product-available="${item.available ? 'true' : 'false'}"
        aria-label="Añadir a favoritos"
        aria-pressed="false"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M20.84 4.61c-1.54-1.43-3.96-1.35-5.4.2L12 8.45 8.56 4.81c-1.44-1.55-3.86-1.63-5.4-.2-1.67 1.55-1.76 4.17-.2 5.84L12 20l9.04-9.55c1.56-1.67 1.47-4.29-.2-5.84Z"></path>
        </svg>
        <span class="gv-favorite-button__text" data-gv-favorite-label>Favorito</span>
      </button>
    `;

    card.innerHTML = `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--card card--media">
          <a class="gangava-product-card__media" href="${escapeAttr(item.url)}" aria-label="${escapeAttr(item.title)}">
            ${favoriteMarkup}
            ${soldMarkup}
            ${badgesMarkup ? `<div class="card__badge bottom left gangava-card-badge-wrap gangava-product-card__badges">${badgesMarkup}</div>` : ''}
            ${imageMarkup}
          </a>
          <div class="gangava-product-card__body">
            <h3 class="gangava-product-card__title">
              <a href="${escapeAttr(item.url)}">${escapeHtml(item.title)}</a>
            </h3>
            <div class="gangava-price">
              <span class="gangava-price__current">${escapeHtml(item.price)}</span>
              ${compareMarkup}
            </div>
            ${savingMarkup}
            <div class="gangava-product-card__stock${stockClass}">
              ${item.available ? 'En stock' : 'Agotado'}
            </div>
            <a class="gangava-card-view-button" href="${escapeAttr(item.url)}">Ver producto</a>
            </div>
        </div>
      </div>
    `;

    return card;
  };

  const init = async (root) => {
    const grid = root.querySelector('[data-gv-recently-viewed-grid]');
    const current = readCurrentProduct(root);
    if (!grid || !current) return;

    const historyBefore = getHistory();
    const existing = historyBefore.filter((item) => item.handle !== current.handle);
    const refreshed = await Promise.all(existing.map((item) => refreshItem(item, root.dataset.currency)));
    const validExisting = refreshed.filter(Boolean).slice(0, MAX_STORED - 1);
    const nextHistory = [current, ...validExisting];
    saveHistory(nextHistory);

    const visible = validExisting.slice(0, MAX_VISIBLE);
    grid.replaceChildren();
    if (!visible.length) {
      root.hidden = true;
      return;
    }

    visible.forEach((item) => grid.appendChild(renderCard(item)));
    root.hidden = false;
    window.dispatchEvent(new CustomEvent('gangava:favorites:changed'));
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-gv-recently-viewed]').forEach((root) => {
      init(root);
    });
  }, { once: true });
})();
